import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Exported for pages that need to build absolute URLs for brokers
// (e.g. signed-URL share flow — see EUH7CompilationPage).
export { BACKEND_URL };

// Get current workspace ID from localStorage
const getWorkspaceId = () => localStorage.getItem('activeWorkspaceId');

// Workspace switch tracking for safety confirmations
const WORKSPACE_SWITCH_KEY = 'lastWorkspaceSwitchTime';
const WORKSPACE_SWITCH_THRESHOLD_MS = 60000; // 60 seconds

const api = axios.create({
  baseURL: API_BASE,
  // iter215 — send the httpOnly access_token cookie on every request
  // so the backend can start migrating callers off localStorage.
  // The Authorization: Bearer header (below) still ships alongside as
  // a backward-compat fallback until localStorage is fully deprecated.
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token and workspace context to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Iter263 Phase R6 — partner_admin / partner_viewer in cross-workspace
  // mode: send X-Partner-Cross-Workspace instead of X-Workspace-Id so the
  // backend aggregates across all their tenants via `resolve_partner_scope`.
  const partnerCrossMode = isPartnerCrossWorkspaceMode();
  // Iter263 U5 hotfix — mirror the same treatment for system_admin
  // Global mode. Stale X-Workspace-Id from a previous session or
  // a switcher hover would otherwise force the backend into a
  // single-tenant query even though the operator has flipped to
  // "All workspaces" in the switcher.
  const sysadminGlobalMode = isCrossWorkspaceMode();
  if (partnerCrossMode) {
    config.headers['X-Partner-Cross-Workspace'] = '1';
    // Force-strip workspace header so backend falls through to
    // partner-scope aggregation.
    delete config.headers['X-Workspace-Id'];
  } else if (sysadminGlobalMode) {
    // Force-strip workspace header so backend falls through to the
    // sysadmin all-workspaces branch (`role == 'system_admin'` → no
    // workspace filter). Same rationale as the partner branch above.
    delete config.headers['X-Workspace-Id'];
  } else if (config.headers['X-Workspace-Id'] === undefined && !config.skipWorkspaceHeader) {
    const workspaceId = getWorkspaceId();
    if (workspaceId) {
      config.headers['X-Workspace-Id'] = workspaceId;
    }
  }
  // Remove the flag so it doesn't get sent to server
  delete config.skipWorkspaceHeader;
  // Remove empty workspace header
  if (config.headers['X-Workspace-Id'] === '') {
    delete config.headers['X-Workspace-Id'];
  }
  return config;
});

// Handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('activeWorkspaceId');
      localStorage.removeItem(WORKSPACE_SWITCH_KEY);
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Helper to build workspace-scoped URL
const wsUrl = (path) => {
  const workspaceId = getWorkspaceId();
  if (!workspaceId) {
    throw new Error('No active workspace. Please select a workspace.');
  }
  return `/workspaces/${workspaceId}${path}`;
};

// Returns true when the System Admin is operating in the "global / all workspaces"
// view. iter224.2 — the explicit `crossWorkspaceMode` flag now wins over a
// stale `activeWorkspaceId` (which can linger from a previous session or
// from AuthContext's boot hydration). Without this fix, sysadmin Global
// mode would silently fall back to their own tenant-scoped data on a
// direct page reload.
export const isCrossWorkspaceMode = () => {
  try {
    if (localStorage.getItem('crossWorkspaceMode') !== 'true') return false;
    const u = JSON.parse(localStorage.getItem('user') || 'null');
    return u?.role === 'system_admin';
  } catch (_e) {
    return false;
  }
};

// Iter263 Phase R6 — partner_admin / partner_viewer can flip an
// equivalent "All my workspaces" toggle. When active, the request
// interceptor swaps the `X-Workspace-Id` header for
// `X-Partner-Cross-Workspace: 1`, and `globalOrWsUrl()` selects the
// global (non-`/workspaces/{id}/…`) endpoint variant.
export const isPartnerCrossWorkspaceMode = () => {
  try {
    if (localStorage.getItem('partnerCrossWorkspaceMode') !== 'true') return false;
    const u = JSON.parse(localStorage.getItem('user') || 'null');
    return u?.role === 'partner_admin' || u?.role === 'partner_viewer';
  } catch (_e) {
    return false;
  }
};

// Helper: pick the global cross-tenant endpoint when System Admin is in
// global mode, otherwise the workspace-scoped path. Used by pages that
// must keep working when no workspace is selected (Audit Logs, Approvals).
//
// Iter263 Phase R6 — extended for partner_admin in cross-workspace mode.
// Their backend endpoints use the global path too because
// `resolve_partner_scope` auto-filters by partner_id when the
// X-Workspace-Id header is absent.
const globalOrWsUrl = (globalPath, wsPath) => {
  if (isCrossWorkspaceMode()) return globalPath;
  if (isPartnerCrossWorkspaceMode()) return globalPath;
  return wsUrl(wsPath);
};

// Workspace management helpers
export const setActiveWorkspace = (workspaceId) => {
  const previousWorkspace = localStorage.getItem('activeWorkspaceId');
  // iter224.2 — allow explicit null/undefined to clear the entry so
  // sysadmin Global mode never persists a stale tenant ID.
  if (workspaceId == null || workspaceId === '') {
    localStorage.removeItem('activeWorkspaceId');
  } else {
    localStorage.setItem('activeWorkspaceId', workspaceId);
  }

  // Track workspace switch time if workspace actually changed
  if (previousWorkspace && previousWorkspace !== workspaceId) {
    localStorage.setItem(WORKSPACE_SWITCH_KEY, Date.now().toString());
  }

  window.dispatchEvent(new CustomEvent('workspaceChanged', { detail: { workspaceId } }));
};

export const getActiveWorkspace = () => localStorage.getItem('activeWorkspaceId');

export const wasWorkspaceRecentlySwitched = () => {
  const switchTime = localStorage.getItem(WORKSPACE_SWITCH_KEY);
  if (!switchTime) return false;
  return (Date.now() - parseInt(switchTime, 10)) < WORKSPACE_SWITCH_THRESHOLD_MS;
};

export const clearWorkspaceSwitchFlag = () => {
  localStorage.removeItem(WORKSPACE_SWITCH_KEY);
};

// Auth (not workspace-scoped)
export const authApi = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  logout: () => api.post('/auth/logout'),
  getMe: () => api.get('/auth/me'),
  getMyWorkspaces: () => api.get('/auth/my-workspaces'),
};

// Tenants / Workspaces (admin operations)
export const tenantsApi = {
  getCurrent: () => api.get(wsUrl('/tenant')),
  updateCurrent: (data) => api.put(wsUrl('/tenant'), data),
  list: () => api.get('/tenants'), // System admin only - not workspace-scoped
};

// Users (workspace-scoped, with admin fallback for global sysadmin mode)
export const usersApi = {
  // iter224 — when sysadmin is in "all workspaces" mode we hit the
  // admin cross-tenant endpoint so the Users page never dead-spins on
  // a missing activeWorkspaceId.
  list: () => api.get(globalOrWsUrl('/admin/users', '/users')),
  create: (data) => api.post(wsUrl('/users'), data),
  updateRole: (userId, role) => api.put(wsUrl(`/users/${userId}/role?role=${role}`)),
  deactivate: (userId) => api.put(wsUrl(`/users/${userId}/deactivate`)),
  invite: (data) => api.post(wsUrl('/users/invite'), data),
};

// Cases (workspace-scoped, with cross-workspace fallback for System Admin)
export const casesApi = {
  approve: (id, body) => api.post(`/cases/${id}/approve`, body || {}),
  list: (params) => api.get(globalOrWsUrl('/admin/cases', '/cases'), { params }),
  // Iter263 Phase N post-QA — partner-scoped list. Used when the caller
  // is partner_admin / partner_viewer (they have no active workspace)
  // OR when the URL carries ``?partner=<id>``. Hits the unscoped
  // ``/cases`` route with the partner filter — backend Phase M4
  // handles the partner→tenant fan-out and cross-partner 403.
  listByPartner: (partnerId, params) =>
    api.get('/cases', {
      params: { ...(params || {}), partner: partnerId },
      skipWorkspaceHeader: true,
    }),
  get: (id) => api.get(wsUrl(`/cases/${id}`)),
  create: (data) => api.post(wsUrl('/cases'), data),
  update: (id, data) => api.put(wsUrl(`/cases/${id}`), data),
  getExceptionQueue: () => api.get(wsUrl('/cases/exceptions/queue')),
  // Soft delete with reason
  archive: (id, reason) => api.post(wsUrl(`/cases/${id}/archive`), { reason }),
  // Restore from archive with reason
  restore: (id, reason) => api.post(wsUrl(`/cases/${id}/restore`), { reason }),
  // Bulk stage change
  bulkStageChange: (caseIds, fromStage, toStage) => 
    api.post(wsUrl('/cases/bulk-stage-change'), { case_ids: caseIds, from_stage: fromStage, to_stage: toStage }),
  // MRN Suggestions (Phase B4)
  getMrnSuggestions: (caseId) => api.get(`/mrn-matching/case/${caseId}/suggestions`),
  saveMrnLink: (importMrn, exportMrn, confidence, notes = null) => 
    api.post('/mrn-matching/save-link', null, { 
      params: { import_mrn: importMrn, export_mrn: exportMrn, confidence, notes } 
    }),
};

// Cases (cross-workspace for System Admin)
export const crossWorkspaceCasesApi = {
  list: (params) => api.get('/cases', { params, skipWorkspaceHeader: true }),
};

// MRN Matching (workspace-scoped)
export const matchingApi = {
  getQueue: (params) => api.get(wsUrl('/matching/queue'), { params }),
  getSuggestedMRNs: (caseId) => api.get(wsUrl(`/matching/suggested-mrns/${caseId}`)),
  linkMRN: (data) => api.post(wsUrl('/matching/link-mrn'), data),
  unlinkMRN: (data) => api.post(wsUrl('/matching/unlink-mrn'), data),
  confirmAndAdvance: (caseId) => api.post(wsUrl(`/matching/confirm-and-advance/${caseId}`)),
  bulkLink: (matches) => api.post(wsUrl('/matching/bulk-link'), { matches }),
  bulkAdvance: (caseIds) => api.post(wsUrl('/matching/bulk-advance'), caseIds),
  autoMatch: (eoriNumber) => api.post(wsUrl(`/matching/auto-match?eori_number=${eoriNumber}`)),
  getBulkSuggestions: (data) => api.post(wsUrl('/matching/bulk-suggest'), data),
  // Rollback match run with reason
  rollbackMatchRun: (matchRunId, reason) => api.post(wsUrl(`/match-runs/${matchRunId}/rollback`), { reason }),
};

// CDS/CHIEF Integration (workspace-scoped)
export const cdsApi = {
  lookupExports: (data) => api.post(wsUrl('/cds/lookup-exports'), data),
  getMRNDetails: (mrn) => api.get(wsUrl(`/cds/mrn-details/${mrn}`)),
  lookupAndCreateExport: (mrn, autoLink = true) => api.post(`/cds/lookup-and-create-export/${mrn}?auto_link=${autoLink}`),
};

// MRN Validation API (workspace-scoped) - RGR compliance checks
export const mrnValidationApi = {
  validate: (data) => api.post('/mrn-validation/validate', data),
  linkWithValidation: (data) => api.post('/mrn-validation/link-with-validation', data),
  overrideAndLink: (data) => api.post('/mrn-validation/override-and-link', data),
  getHistory: (caseId) => api.get(`/mrn-validation/history/${caseId}`),
  getSampleData: () => api.get('/mrn-validation/sample-data'),
  // Bulk validation for Import Review
  bulkValidate: (data) => api.post('/mrn-validation/bulk-validate', data),
  getBulkValidationHistory: (caseId) => api.get(`/mrn-validation/bulk-validation-history/${caseId}`),
  // Bulk override
  bulkOverride: (data) => api.post('/mrn-validation/bulk-override', data),
  getBulkOverrideHistory: (caseId) => api.get(`/mrn-validation/bulk-override-history/${caseId}`),
};

// UK Trade Tariff API - Live HS Code Lookup and Duty Rates
export const tariffApi = {
  lookup: (commodityCode) => api.get(`/tariff/lookup/${commodityCode}`),
  getChapters: () => api.get('/tariff/chapters'),
  getChapter: (chapterCode) => api.get(`/tariff/chapter/${chapterCode}`),
  search: (query, limit = 10) => api.get('/tariff/search', { params: { q: query, limit } }),
  validate: (commodityCode) => api.get(`/tariff/validate/${commodityCode}`),
  compare: (importCode, exportCode) => api.post('/tariff/compare', null, { 
    params: { import_code: importCode, export_code: exportCode } 
  }),
};

// Export Declarations API (workspace-scoped) - For MRN validation linking
export const exportDeclarationsApi = {
  create: (data) => api.post('/export-declarations/', data),
  uploadCsv: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/export-declarations/upload-csv', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  list: (params = {}) => api.get('/export-declarations/', { params }),
  get: (id) => api.get(`/export-declarations/${id}`),
  update: (id, data) => api.put(`/export-declarations/${id}`, data),
  link: (data) => api.post('/export-declarations/link', data),
  unlink: (exportId, importId) => api.delete(`/export-declarations/link?export_id=${exportId}&import_id=${importId}`),
  findByMrn: (mrn) => api.get(`/export-declarations/find-by-mrn/${mrn}`),
  getSampleTemplate: () => api.get('/export-declarations/sample-csv-template'),
};

// Iter249 — Shipments API (workspace-scoped)
export const shipmentsApi = {
  list: (params = {}) => api.get(wsUrl('/shipments'), { params }),
  get: (id) => api.get(wsUrl(`/shipments/${id}`)),
  sync: () => api.post(wsUrl('/shipments/sync')),
  lastRun: () => api.get(wsUrl('/shipments/_sync/last-run')),
};

// Enhanced Intake Workflow API (workspace-scoped)
export const intakeWorkflowApi = {
  scan: (data) => api.post('/intake-workflow/scan', data),
  verify: (data) => api.post('/intake-workflow/verify', data),
  setCondition: (data) => api.post('/intake-workflow/condition', data),
  getPhotoRequirements: (caseId) => api.get(`/intake-workflow/photo-requirements/${caseId}`),
  createException: (data) => api.post('/intake-workflow/exception', data),
  confirmComplete: (data) => api.post('/intake-workflow/confirm', data),
  getDamageCodes: () => api.get('/intake-workflow/damage-codes'),
  getExceptionTypes: () => api.get('/intake-workflow/exception-types'),
  getSampleData: () => api.get('/intake-workflow/sample-data'),
  generateTestCases: (count = 5) => api.post(`/intake-workflow/generate-test-cases?count=${count}`),
};

// Imports & Automated MRN Matching (workspace-scoped)
export const importsApi = {
  // Returns CSV (Client Admin) - staging only, no confirmation needed
  uploadReturns: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(wsUrl('/imports/returns/upload'), formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  // Exports CSV (Operator) - staging only, no confirmation needed
  uploadExports: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(wsUrl('/imports/exports/upload'), formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  // Staging inbox
  getInbox: (params) => api.get(wsUrl('/imports/inbox'), { params }),
  getImportDetails: (type, id) => api.get(wsUrl(`/imports/${type}/${id}`)),
  // Get import preview for confirmation dialog
  getImportPreview: (type, id) => api.get(wsUrl(`/imports/${type}/${id}/preview`)),
  // Automated matching
  runMatching: (returnsId, exportsId) => api.post(wsUrl(`/imports/match/run?returns_import_id=${returnsId}&exports_import_id=${exportsId}`)),
  // Match against existing cases (from Sendcloud, ERP, etc.)
  runMatchingExistingCases: (exportsId) => api.post(wsUrl(`/imports/match/run?exports_import_id=${exportsId}&match_existing_cases=true`)),
  getMatchSessions: () => api.get(wsUrl('/imports/match/sessions')),
  getMatchSession: (id) => api.get(wsUrl(`/imports/match/sessions/${id}`)),
  makeMatchDecision: (sessionId, returnRow, decision, selectedMrn, reason) => 
    api.post(wsUrl(`/imports/match/decision?session_id=${sessionId}&return_row=${returnRow}&decision=${decision}${selectedMrn ? `&selected_mrn=${selectedMrn}` : ''}${reason ? `&reason=${encodeURIComponent(reason)}` : ''}`)),
  applyMatches: (sessionId) => api.post(wsUrl(`/imports/match/apply?session_id=${sessionId}`)),
  // Legacy methods for DataImportPage
  stageReturns: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(wsUrl('/imports/stage'), formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  listStaged: () => api.get(wsUrl('/imports/staged')),
  getStaged: (id) => api.get(wsUrl(`/imports/staged/${id}`)),
  validateAndImport: (id) => api.post(wsUrl(`/imports/staged/${id}/validate`)),
  list: () => api.get(wsUrl('/imports')),
};

// Filing Datasets (workspace-scoped)
export const filingApi = {
  generateDataset: (data) => api.post(wsUrl('/filing/generate-dataset'), data),
  getDatasets: () => api.get(wsUrl('/filing/datasets')),
  getDataset: (id) => api.get(wsUrl(`/filing/datasets/${id}`)),
  downloadDataset: (id) => `${BACKEND_URL}/api/workspaces/${getWorkspaceId()}/filing/datasets/${id}/download`,
  submitDataset: (id) => api.post(wsUrl(`/filing/datasets/${id}/submit`)),
};

// Evidence (workspace-scoped)
export const evidenceApi = {
  getUploadUrl: (data) => api.post(globalOrWsUrl('/evidence/upload-url', '/evidence/upload-url'), data),
  completeUpload: (id) => api.post(globalOrWsUrl(`/evidence/${id}/complete`, `/evidence/${id}/complete`)),
  getDownloadUrl: (id) => api.get(wsUrl(`/evidence/${id}/download-url`)),
  listForCase: (caseId) => api.get(wsUrl(`/cases/${caseId}/evidence`)),
  getChecklist: (caseId) => api.get(wsUrl(`/evidence/checklist/${caseId}`)),
};

// Decisions (workspace-scoped)
export const decisionsApi = {
  create: (data) => api.post(wsUrl('/decisions'), data),
  listForCase: (caseId) => api.get(wsUrl(`/cases/${caseId}/decisions`)),
};

// Dashboard (workspace-scoped, falls back to cross-workspace totals
// for System Admin in global / all-workspaces mode)
export const dashboardApi = {
  getSummary: () => isCrossWorkspaceMode()
    ? api.get('/dashboard/summary', { skipWorkspaceHeader: true })
    : api.get(wsUrl('/dashboard/summary')),
  getByMarket: () => isCrossWorkspaceMode()
    ? api.get('/dashboard/by-market', { skipWorkspaceHeader: true })
    : api.get(wsUrl('/dashboard/by-market')),
  getByCategory: () => isCrossWorkspaceMode()
    ? api.get('/dashboard/by-category', { skipWorkspaceHeader: true })
    : api.get(wsUrl('/dashboard/by-category')),
  getTimeline: (days = 30) => isCrossWorkspaceMode()
    ? api.get(`/dashboard/timeline?days=${days}`, { skipWorkspaceHeader: true })
    : api.get(wsUrl(`/dashboard/timeline?days=${days}`)),
};

// Dashboard (cross-workspace for System Admin)
export const crossWorkspaceDashboardApi = {
  // These use header-based endpoints without workspace ID for cross-workspace totals
  getSummary: () => api.get('/dashboard/summary', { skipWorkspaceHeader: true }),
  getByMarket: () => api.get('/dashboard/by-market', { skipWorkspaceHeader: true }),
  getByCategory: () => api.get('/dashboard/by-category', { skipWorkspaceHeader: true }),
  getTimeline: (days = 30) => api.get(`/dashboard/timeline?days=${days}`, { skipWorkspaceHeader: true }),
};

// Reports (workspace-scoped)
export const reportsApi = {
  downloadFinancePack: () => api.get(wsUrl('/reports/finance-pack')),
  downloadCompliancePack: () => api.get(wsUrl('/reports/compliance-pack')),
  getExportHistory: () => api.get(wsUrl('/reports/export-history')),
};

// Audit Logs (workspace-scoped, with cross-workspace fallback for System Admin)
export const auditLogsApi = {
  list: (params) => api.get(globalOrWsUrl('/admin/audit-logs', '/audit-logs'), { params }),
  // Wave 5 (iter172) — server-side CSV export honours broker scope + full filter set.
  exportCsv: (params) => api.get(wsUrl('/audit-logs/export.csv'), {
    params, responseType: 'blob',
  }),
};

// Signed downloads (Wave 6, iter181) — short-lived HMAC URLs that
// don't need a Bearer once minted. Prefer these for anything
// user-facing so JWTs never leak into browser history, referrers or
// server access logs.
export const downloadsApi = {
  sign: (body) => api.post('/downloads/sign', body),
};

// Billing (Wave 7, iter183) — Stripe subscription lifecycle for the
// current workspace. `plans` is public (no auth); everything else
// requires the caller's Bearer + X-Workspace-Id.
export const billingApi = {
  listPlans: (opts = {}) =>
    api.get('/billing/plans', { params: opts.internal ? { internal: 1 } : {} }),
  getSubscription: () => api.get('/billing/subscription'),
  // Payment gateway removed from the upgrade path — upgrades are invoiced manually.
  adminGetSubscription: (workspaceId) =>
    api.get(`/billing/admin/subscription/${workspaceId}`),
  // Iter232 — sysadmin coupon dropdown + enable-checkout with discount.
  adminListCoupons: () => api.get('/billing/admin/coupons', { skipWorkspaceHeader: true }),
  adminBootstrapCoupons: () => api.post('/billing/admin/coupons/bootstrap', {}, { skipWorkspaceHeader: true }),
  adminEnableCheckout: (workspaceId, body) =>
    api.post(`/billing/admin/workspaces/${workspaceId}/enable-checkout`, body, { skipWorkspaceHeader: true }),
};

// Iter220 — Invite-only sandbox provisioning + admin state-machine controls.
export const sandboxInvitesApi = {
  list: (status) => api.get('/admin/sandbox-invites', { params: status ? { status } : {} }),
  create: (body) => api.post('/admin/sandbox-invites', body),
  resend: (id) => api.post(`/admin/sandbox-invites/${id}/resend`),
  revoke: (id) => api.post(`/admin/sandbox-invites/${id}/revoke`),
  preview: (token) => api.get(`/sandbox-invites/${token}`, { skipWorkspaceHeader: true }),
  redeem: (token, body) => api.post(`/sandbox-invites/${token}/redeem`, body, { skipWorkspaceHeader: true }),
  extendSandbox: (workspaceId, body) => api.post(`/admin/workspaces/${workspaceId}/extend-sandbox`, body),
  convertToTrial: (workspaceId, body) => api.post(`/admin/workspaces/${workspaceId}/convert-to-trial`, body),
  setTrialCheckoutFlag: (workspaceId, enabled) => api.post(
    `/admin/workspaces/${workspaceId}/set-trial-checkout-enabled`,
    { enabled },
  ),
};

// Iter219 — Early Access lead capture (public POST, sysadmin queue mgmt).
export const accessRequestsApi = {
  submit: (body) => api.post('/access-requests', body, { skipWorkspaceHeader: true }),
  list: (status) => api.get('/access-requests', { params: status ? { status } : {} }),
  get: (id) => api.get(`/access-requests/${id}`),
  markContacted: (id) => api.post(`/access-requests/${id}/mark-contacted`),
  decline: (id, reason) => api.post(`/access-requests/${id}/decline`, { reason }),
};

// Trial onboarding (Wave 7 Phase 2, iter186) — Merchant vs Broker
// selection + sandbox demo clients. Every trial workspace is a sandbox
// (`is_sandbox=true`). After a user picks a mode the launcher stops
// gating on the onboarding step.
export const trialApi = {
  getStatus: () => api.get('/trial/status'),
  setMode: (body) => api.post('/trial/mode', body),
  listDemoClients: () => api.get('/trial/demo-clients'),
  createDemoClient: (body) => api.post('/trial/demo-clients', body),
};

// Broker client detail (Wave 7 Phase 2d, iter190) — sandbox demo
// client view (cases + evidence packs + claim packs + KPIs).
export const brokerClientsApi = {
  getDetail: (clientId) => api.get(`/broker/clients/${clientId}`),
  getCaseDetail: (clientId, caseId) => api.get(`/broker/clients/${clientId}/cases/${caseId}`),
  completeItem: (clientId, caseId, body) =>
    api.post(`/broker/clients/${clientId}/cases/${caseId}/complete-item`, body),
};

// Broker assignments (iter216, Feb 2026) — Merchant Admin scopes a
// broker's access to specific cases/claims/audits/extractions.
export const brokerAssignmentsApi = {
  list: () => api.get('/broker-assignments'),
  create: (body) => api.post('/broker-assignments', body),
  update: (assignmentId, patch) =>
    api.patch(`/broker-assignments/${assignmentId}`, patch),
  revoke: (assignmentId) => api.delete(`/broker-assignments/${assignmentId}`),
};

// Upgrade requests (Wave 7 Phase 3, iter191) — Operators / Viewers
// ping their Merchant Admin instead of running checkout themselves.
export const upgradeRequestsApi = {
  create: (body) => api.post('/upgrade-requests', body),
  list: (params) => api.get('/upgrade-requests', { params }),
  get: (id) => api.get(`/upgrade-requests/${id}`),
  dismiss: (id, body) => api.post(`/upgrade-requests/${id}/dismiss`, body || {}),
  acknowledge: (id) => api.post(`/upgrade-requests/${id}/acknowledge`),
};

// iter225 — sysadmin Trial Requests inbox (cross-tenant).
export const adminUpgradeRequestsApi = {
  list: (params) => api.get('/admin/upgrade-requests', { params, skipWorkspaceHeader: true }),
  get: (id) => api.get(`/admin/upgrade-requests/${id}`, { skipWorkspaceHeader: true }),
  convert: (id, body) => api.post(`/admin/upgrade-requests/${id}/convert`, body || {}, { skipWorkspaceHeader: true }),
  reject: (id, body) => api.post(`/admin/upgrade-requests/${id}/reject`, body || {}, { skipWorkspaceHeader: true }),
  createInvoice: (id, body) => api.post(`/admin/upgrade-requests/${id}/invoice`, body, { skipWorkspaceHeader: true }),
  activate: (id, body) => api.post(`/admin/upgrade-requests/${id}/activate`, body || {}, { skipWorkspaceHeader: true }),
};

// Manual invoicing — sysadmin raises invoices, workspace downloads PDFs.
export const adminManualInvoicesApi = {
  list: (params) => api.get('/admin/invoices', { params, skipWorkspaceHeader: true }),
  markPaid: (id, body) => api.post(`/admin/invoices/${id}/mark-paid`, body, { skipWorkspaceHeader: true }),
  void: (id) => api.post(`/admin/invoices/${id}/void`, {}, { skipWorkspaceHeader: true }),
  getSettings: () => api.get('/admin/invoice-settings', { skipWorkspaceHeader: true }),
  saveSettings: (body) => api.put('/admin/invoice-settings', body, { skipWorkspaceHeader: true }),
};
export const invoicesApi = {
  list: () => api.get('/invoices'),
  downloadPdf: (id) => api.get(`/invoices/${id}/pdf`, { responseType: 'blob' }),
};

// iter231 — Prospects Cockpit (merged sales timeline across access
// requests, sandbox invites, and trial requests). Sysadmin only.
export const adminProspectsApi = {
  list: (params) => api.get('/admin/prospects', { params, skipWorkspaceHeader: true }),
  get: (email) => api.get(`/admin/prospects/${encodeURIComponent(email)}`, { skipWorkspaceHeader: true }),
};

// iter258 — Connector requests. Client-role users can ask Meridian to
// connect an integration on their behalf. System_admin reads the
// global open queue for the Prospects/Growth cockpit.
export const workspaceConnectorRequestsApi = {
  create: (workspaceId, body) =>
    api.post(`/workspaces/${workspaceId}/connector-requests`, body),
  list: (workspaceId, params) =>
    api.get(`/workspaces/${workspaceId}/connector-requests`, { params }),
};
export const adminConnectorRequestsApi = {
  list: (params) => api.get('/connector-requests', { params, skipWorkspaceHeader: true }),
};

// iter233 — Banded Stripe prices + invoice endpoint.
export const adminBandsApi = {
  list: () => api.get('/billing/admin/bands', { skipWorkspaceHeader: true }),
  bootstrap: () => api.post('/billing/admin/bands/bootstrap', {}, { skipWorkspaceHeader: true }),
};
export const adminAddonTemplatesApi = {
  list: () => api.get('/billing/admin/addon-templates', { skipWorkspaceHeader: true }),
};
export const adminInvoicesApi = {
  create: (workspaceId, body) =>
    api.post(`/billing/admin/workspaces/${workspaceId}/invoices`, body, { skipWorkspaceHeader: true }),
};

// Iter 234 — H7 €3 flat-duty reclaim (eligibility engine + aggregation +
// country-templated exports + evidence pack ZIP).
export const euH7Api = {
  // Eligibility
  seedReasonMapping: () => api.post(wsUrl('/eu/reason-mapping/seed'), {}),
  listReasonMapping: () => api.get(wsUrl('/eu/reason-mapping')),
  listUnmappedReasons: () => api.get(wsUrl('/eu/reason-mapping/unmapped')),
  upsertReasonMapping: (body) => api.post(wsUrl('/eu/reason-mapping'), body),
  getClaimWindow: () => api.get(wsUrl('/eu/claim-window')),
  setClaimWindow: (days) => api.post(wsUrl('/eu/claim-window'), { days }),
  evaluateItemLine: (itemLineId) =>
    api.post(wsUrl(`/eu/item-lines/${encodeURIComponent(itemLineId)}/evaluate-eligibility`), {}),
  evaluateBatch: () => api.post(wsUrl('/eu/evaluate-eligibility/batch'), {}),
  // Claims
  compile: (body) => api.post(wsUrl('/eu/h7-claims/compile'), body),
  list: (params) => api.get(wsUrl('/eu/h7-claims'), { params }),
  get: (claimId) => api.get(wsUrl(`/eu/h7-claims/${claimId}`)),
  templateCoverage: () => api.get(wsUrl('/eu/h7-templates/coverage')),
  analytics: (claimId) => api.get(wsUrl(`/eu/h7-claims/${claimId}/analytics`)),
  wholeClaimZipUrl: (workspaceId, claimId, country, fmt, includeGeneric) =>
    `/api/workspaces/${workspaceId}/eu/h7-claims/${claimId}/whole-claim-zip`
    + `?country=${country}&fmt=${fmt}&include_generic=${includeGeneric ? 'true' : 'false'}`,
  exportUrl: (workspaceId, claimId, country, fmt) =>
    `/api/workspaces/${workspaceId}/eu/h7-claims/${claimId}/export/${country}.${fmt}`,
  // Evidence
  evidenceManifest: (claimId, h7LineKey) =>
    api.get(wsUrl(
      `/eu/h7-claims/${claimId}/lines/${encodeURIComponent(h7LineKey)}/evidence-manifest`,
    )),
  evidenceZipUrl: (workspaceId, claimId, h7LineKey) =>
    `/api/workspaces/${workspaceId}/eu/h7-claims/${claimId}/lines/${encodeURIComponent(h7LineKey)}/evidence-zip`,
  signEvidenceZip: (body) => api.post(wsUrl('/eu/h7-evidence-zip/sign'), body),
  // DDP charges
  uploadDDPCharges: (formData) =>
    api.post(wsUrl('/eu/ddp-charges/upload-csv'), formData,
             { headers: { 'content-type': 'multipart/form-data' } }),
  listDDPCharges: (params) => api.get(wsUrl('/eu/ddp-charges'), { params }),
  listUnjoinedDDPCharges: (params) => api.get(wsUrl('/eu/ddp-charges/unjoined'), { params }),
};

// Workspace integrations / connectors (iter197 — Merchant Admin self-serve).
// Every route is workspace-scoped on the backend so a merchant admin can
// only ever touch their own tenant's connectors.
export const connectorsApi = {
  listAvailable: (params) =>
    api.get(wsUrl('/connectors/available'), { params }),
  listInstalled: () => api.get(wsUrl('/connectors')),
  install: (body) => api.post(wsUrl('/connectors'), body),
  get: (id) => api.get(wsUrl(`/connectors/${id}`)),
  update: (id, body) => api.put(wsUrl(`/connectors/${id}`), body),
  remove: (id) => api.delete(wsUrl(`/connectors/${id}`)),
  test: (id) => api.post(wsUrl(`/connectors/${id}/test`)),
  sync: (id, body) => api.post(wsUrl(`/connectors/${id}/sync`), body),
  syncHistory: (id) => api.get(wsUrl(`/connectors/${id}/sync-history`)),
};

// Analytics (Wave 7 Phase 2b, iter188) — lightweight upsell telemetry.
// Fire-and-forget; failures are swallowed so tracking never breaks UX.
export const analyticsApi = {
  track: (body) => api.post('/analytics/events', body),
  getSummary: ({ days = 7 } = {}) =>
    api.get('/analytics/summary', { params: { days } }),
};

// Data Quality (workspace-scoped)
export const dataQualityApi = {
  getSummary: () => api.get(wsUrl('/data-quality/summary')),
};

// Tasks (workspace-scoped)
export const tasksApi = {
  list: (params) => api.get(wsUrl('/tasks'), { params }),
  create: (data) => api.post(wsUrl('/tasks'), data),
  complete: (id) => api.put(wsUrl(`/tasks/${id}/complete`)),
};

// Comments (workspace-scoped)
export const commentsApi = {
  list: (caseId) => api.get(wsUrl('/comments'), { params: { case_id: caseId } }),
  create: (data) => api.post(wsUrl('/comments'), data),
};

// Integrations (workspace-scoped, Mocked)
export const integrationsApi = {
  syncSendcloud: () => api.post(wsUrl('/integrations/sendcloud/sync')),
  submitToCds: (caseIds) => api.post(wsUrl('/integrations/cds/submit'), caseIds),
  getCdsStatus: (submissionId) => api.get(wsUrl(`/integrations/cds/status/${submissionId}`)),
};

// Notifications (workspace-scoped)
export const notificationsApi = {
  list: (params = {}) => api.get('/notifications', { params }),
  getStats: () => api.get('/notifications/stats'),
  getUnreadCount: () => api.get('/notifications/stats').then(res => ({ data: { count: res.data.unread } })),
  markAsRead: (id) => api.post(`/notifications/${id}/read`),
  markAllAsRead: () => api.post('/notifications/mark-all-read'),
  dismiss: (id) => api.delete(`/notifications/${id}`),
  clearAll: (readOnly = true) => api.delete(`/notifications?read_only=${readOnly}`),
};

// Workspace-scoped notifications (threshold alerts, etc.)
export const workspaceNotificationsApi = {
  list: (limit = 20) => api.get(wsUrl(`/notifications?limit=${limit}`)),
  markAsRead: (id) => api.post(wsUrl(`/notifications/${id}/read`)),
  markAllAsRead: () => api.post(wsUrl('/notifications/mark-all-read')),
};

// Workspace Settings API
export const workspaceSettingsApi = {
  get: () => api.get(wsUrl('/settings')),
  update: (data) => api.put(wsUrl('/settings'), data),
};

// Import Review Threshold Alerts
export const importReviewAlertsApi = {
  getHighValueCases: (limit = 10) => api.get(wsUrl(`/import-review/high-value-cases?limit=${limit}`)),
  getCaseAlertStatus: (caseId) => api.get(wsUrl(`/import-review/cases/${caseId}/alert-status`)),
  computeThreshold: (caseId) => api.post(wsUrl(`/import-review/cases/${caseId}/compute-threshold`)),
};

// Historical Audit API (workspace-scoped)
export const historicalAuditApi = {
  // Convert opportunity to claim case
  convertOpportunityToClaim: (auditCaseId, opportunityId, notes = null) => 
    api.post(wsUrl(`/import-review/audit-cases/${auditCaseId}/opportunities/${opportunityId}/convert-to-claim`), 
      notes ? { notes } : {}),
  // Get derived claim cases from an audit
  getDerivedClaims: (auditCaseId) => api.get(wsUrl(`/import-review/audit-cases/${auditCaseId}/derived-claims`)),
};

// Workspace Activity Feed (workspace-scoped)
export const activityApi = {
  getFeed: (category = null, limit = 30) => {
    const params = new URLSearchParams();
    if (category) params.append('category', category);
    params.append('limit', limit.toString());
    return api.get(wsUrl(`/activity?${params.toString()}`));
  },
};

// Approvals Queue (Cross-workspace for Approvers)
export const approvalsQueueApi = {
  getQueue: () => api.get('/approvals-queue'),
};

// 3PL Management API (System Admin only)
export const threePLApi = {
  // Organizations
  getOrganizations: (params) => api.get('/admin/3pl/organizations', { params }),
  getOrganization: (orgId) => api.get(`/admin/3pl/organizations/${orgId}`),
  createOrganization: (data) => api.post('/admin/3pl/organizations', data),
  updateOrganization: (orgId, data) => api.put(`/admin/3pl/organizations/${orgId}`, data),
  archiveOrganization: (orgId) => api.post(`/admin/3pl/organizations/${orgId}/archive`),
  restoreOrganization: (orgId) => api.post(`/admin/3pl/organizations/${orgId}/restore`),
  
  // Sites
  getSites: (params) => api.get('/admin/3pl/sites', { params }),
  getSite: (siteId) => api.get(`/admin/3pl/sites/${siteId}`),
  createSite: (data) => api.post('/admin/3pl/sites', data),
  updateSite: (siteId, data) => api.put(`/admin/3pl/sites/${siteId}`, data),
  archiveSite: (siteId) => api.delete(`/admin/3pl/sites/${siteId}`),
  restoreSite: (siteId) => api.post(`/admin/3pl/sites/${siteId}/restore`),
  
  // User-Site Assignments
  getUserSites: (userId) => api.get(`/admin/3pl/users/${userId}/sites`),
  assignUserToSite: (userId, siteId, roleAtSite) => 
    api.post('/admin/3pl/user-site-assignments', { user_id: userId, site_id: siteId, role_at_site: roleAtSite }),
  removeUserFromSite: (userId, siteId) => 
    api.delete(`/admin/3pl/user-site-assignments/${userId}/${siteId}`),
  
  // Workspace default site
  setWorkspaceDefaultSite: (workspaceId, siteId) => 
    api.put(`/admin/workspaces/${workspaceId}/default-site?site_id=${siteId}`),
  removeWorkspaceDefaultSite: (workspaceId) => 
    api.delete(`/admin/workspaces/${workspaceId}/default-site`),
};

// 3PL Operator API (site-filtered)
export const threePLOperatorApi = {
  getMySites: () => api.get('/3pl/my-sites'),
  getIntakeCases: (params) => api.get('/3pl/intake/cases', { params }),
  getIntakeSummary: () => api.get('/3pl/intake/summary'),
};

// Intake Exceptions
export const intakeExceptionsApi = {
  create: (data) => api.post('/intake/exceptions', data),
  list: (params) => api.get('/intake/exceptions', { params }),
  update: (exceptionId, data) => api.put(`/intake/exceptions/${exceptionId}`, data),
};

// Site Override (Meridian Operator only)
export const siteOverrideApi = {
  override: (workspaceId, caseId, newSiteId, reason) => 
    api.post(`/workspaces/${workspaceId}/cases/${caseId}/override-site`, { 
      case_id: caseId, 
      new_site_id: newSiteId, 
      reason 
    }),
};

// Admin API (System Admin only - not workspace-scoped)
export const adminApi = {
  getPlatformStats: () => api.get('/admin/platform-stats'),
  getCases: (params) => api.get('/admin/cases', { params }),
  getWorkspaces: () => api.get('/admin/workspaces'),
  
  // User management
  getAllUsers: (params) => api.get('/admin/users', { params }),
  getInternalUsers: () => api.get('/admin/internal-users'),
  
  // Workspace assignments
  getUserWorkspaces: (userId) => api.get(`/admin/users/${userId}/workspaces`),
  assignWorkspace: (userId, workspaceId, roleInWorkspace) => 
    api.post(`/admin/users/${userId}/workspaces`, { 
      user_id: userId, 
      workspace_id: workspaceId,
      role_in_workspace: roleInWorkspace 
    }),
  removeWorkspace: (userId, workspaceId) => 
    api.delete(`/admin/users/${userId}/workspaces/${workspaceId}`),
  getWorkspaceUsers: (workspaceId) => api.get(`/admin/workspaces/${workspaceId}/users`),
};

// Home App Launcher (Wave 8.1, iter173)
export const overviewApi = {
  get: () => api.get('/overview'),
  global: () => api.get('/overview/global', { skipWorkspaceHeader: true }),
};
export const homeApi = {
  getKpis: () => api.get('/home/kpis'),
  getTiles: () => api.get('/home/tiles'),
};

// Iter236 — Nav Pins (per-user, per-workspace persistence).
export const navPinsApi = {
  get: () => api.get('/nav-pins'),
  put: (pinIds) => api.put('/nav-pins', { pin_ids: pinIds }),
};

// Insights Dashboard (Wave 8.3, iter176)
export const insightsApi = {
  kpis: (params) => api.get('/insights/kpis', { params }),
  pipeline: (params) => api.get('/insights/pipeline', { params }),
  evidence: (params) => api.get('/insights/evidence', { params }),
  cycleTime: (params) => api.get('/insights/cycle-time', { params }),
  exceptions: (params) => api.get('/insights/exceptions', { params }),
  recovery: (params) => api.get('/insights/recovery', { params }),
  fxRates: (params) => api.get('/insights/fx-rates', { params }),
  // Iter260 — SOP KPIs (Compliance Excellence strip)
  sopKpis: (params) => api.get('/insights/sop-kpis', { params }),
};

// Iter260b — HMRC preflight (sysadmin cross-workspace summary tile)
export const preflightApi = {
  perWorkspace: () => api.post('/cds-platform/filing-context/preflight'),
  summary: () => api.get('/cds-platform/filing-context/preflight-summary'),
};

// Iter260b — Governed exception object on a CaseItem (meridian_approver only)
export const approvedExceptionApi = {
  create: (caseItemId, body) => api.post(`/case-items/${caseItemId}/approved-exception`, body),
  update: (caseItemId, body) => api.patch(`/case-items/${caseItemId}/approved-exception`, body),
  remove: (caseItemId) => api.delete(`/case-items/${caseItemId}/approved-exception`),
};

// Iter260 / Iter260b — Inbound shipment legs on RGR cases
export const shipmentLegApi = {
  list: (workspaceId, caseId) =>
    api.get(`/workspaces/${workspaceId}/rgr-cases/${caseId}/shipment-leg`),
  create: (workspaceId, caseId, body) =>
    api.post(`/workspaces/${workspaceId}/rgr-cases/${caseId}/shipment-leg`, body),
  patch: (workspaceId, legId, body) =>
    api.patch(`/workspaces/${workspaceId}/rgr-cases/shipment-legs/${legId}`, body),
};

// Iter263 Phase C — Partner Dashboard roll-up
export const partnerApi = {
  getRollup: (partnerId) =>
    api.get(`/partners/${partnerId}/rollup`, { skipWorkspaceHeader: true }),
  rollupCsvUrl: (partnerId) =>
    `${API_BASE}/partners/${partnerId}/rollup.csv`,
  workspacePackZipUrl: (partnerId, workspaceId, category) => {
    const base = `${API_BASE}/partners/${partnerId}/workspaces/${workspaceId}/pack.zip`;
    if (category && String(category).toLowerCase() !== 'all') {
      return `${base}?category=${encodeURIComponent(category)}`;
    }
    return base;
  },
  // Iter263 Phase I1 — serial number search across a partner's workspaces
  searchReturnsBySerial: (partnerId, serial) =>
    api.get(`/partners/${partnerId}/returns/search`, {
      params: { serial },
      skipWorkspaceHeader: true,
    }),
  listWorkspaces: (partnerId) =>
    api.get(`/partners/${partnerId}/workspaces`, { skipWorkspaceHeader: true }),
  getWorkspace: (partnerId, workspaceId) =>
    api.get(`/partners/${partnerId}/workspaces/${workspaceId}`,
             { skipWorkspaceHeader: true }),
  // Iter263 Phase D — Workspace provisioning + invites
  createWorkspace: (partnerId, body) =>
    api.post(`/partners/${partnerId}/workspaces`, body,
              { skipWorkspaceHeader: true }),
  inviteWorkspaceUser: (partnerId, workspaceId, body) =>
    api.post(`/partners/${partnerId}/workspaces/${workspaceId}/invites`, body,
              { skipWorkspaceHeader: true }),
  // Iter263 Phase E — status, members, conversion
  getStatus: (partnerId) =>
    api.get(`/partners/${partnerId}/status`, { skipWorkspaceHeader: true }),
  listMembers: (partnerId, workspaceId) =>
    api.get(`/partners/${partnerId}/workspaces/${workspaceId}/members`,
             { skipWorkspaceHeader: true }),
  revokeMember: (partnerId, workspaceId, userId, body) =>
    api.post(
      `/partners/${partnerId}/workspaces/${workspaceId}/members/${userId}/revoke`,
      body, { skipWorkspaceHeader: true }),
  // Iter263 Phase G — resend an expired / unredeemed invite
  resendInvite: (partnerId, workspaceId, userId) =>
    api.post(
      `/partners/${partnerId}/workspaces/${workspaceId}/invites/${userId}/resend`,
      {}, { skipWorkspaceHeader: true }),
  requestConversion: (partnerId) =>
    api.post(`/partners/${partnerId}/conversion-request`, {},
              { skipWorkspaceHeader: true }),
};

// Iter263 Phase N1 — Sysadmin scope sessions
export const scopeSessionApi = {
  enter: (partnerId, body) =>
    api.post(`/admin/partners/${partnerId}/scope-enter`, body,
              { skipWorkspaceHeader: true }),
  exit: (sessionId) =>
    api.post(`/admin/scope-sessions/${sessionId}/exit`, {},
              { skipWorkspaceHeader: true }),
  active: () =>
    api.get('/admin/scope-sessions/active', { skipWorkspaceHeader: true }),
  scopedLogs: (params) =>
    api.get('/admin/audit-logs/scoped', { params,
                                            skipWorkspaceHeader: true }),
};

// Iter263 Phase N2 — Partner Settings surfaces
export const partnerSettingsApi = {
  listTeam: (partnerId) =>
    api.get(`/partners/${partnerId}/team`, { skipWorkspaceHeader: true }),
  inviteTeam: (partnerId, body) =>
    api.post(`/partners/${partnerId}/team/invite`, body,
              { skipWorkspaceHeader: true }),
  revokeTeam: (partnerId, userId) =>
    api.post(`/partners/${partnerId}/team/${userId}/revoke`, {},
              { skipWorkspaceHeader: true }),
  listAuditLogs: (partnerId, params) =>
    api.get(`/partners/${partnerId}/audit-logs`, {
      params, skipWorkspaceHeader: true,
    }),
};

// Iter263 Phase M5 / N — Lane entitlements (per-partner)
export const laneEntitlementsApi = {
  get: (partnerId) =>
    api.get(`/partners/${partnerId}/lane-entitlements`,
             { skipWorkspaceHeader: true }),
  update: (partnerId, body) =>
    api.patch(`/admin/partners/${partnerId}/lane-entitlements`, body,
                { skipWorkspaceHeader: true }),
  // Iter S25 Phase 27 — self-serve workspace-level lane toggle.
  // Body: { enabled_lanes: [...] }. Server enforces all four gates
  // (global flag / partner entitlement / role / partner scope).
  updateWorkspaceEnabledLanes: (workspaceId, enabledLanes) =>
    api.patch(`/workspaces/${workspaceId}/enabled-lanes`,
                { enabled_lanes: enabledLanes },
                { skipWorkspaceHeader: true }),
};

// Iter263 Phase N3 — Global feature flags (US_LANE_LIVE toggle)
export const featureFlagsApi = {
  list: () => api.get('/admin/feature-flags',
                        { skipWorkspaceHeader: true }),
  toggle: (flagId, enabled) =>
    api.post(`/admin/feature-flags/${flagId}/toggle`, { enabled },
              { skipWorkspaceHeader: true }),
};

// Iter263 Phase N4 — Bulk merchant invite (sysadmin)
export const partnerBulkInviteApi = {
  preview: (partnerId, rows, opts = {}) =>
    api.post(`/admin/partners/${partnerId}/bulk-invite/preview`,
              { rows, ...opts }, { skipWorkspaceHeader: true }),
  commit: (partnerId, rows, opts = {}) =>
    api.post(`/admin/partners/${partnerId}/bulk-invite/commit`,
              { rows, ...opts }, { skipWorkspaceHeader: true }),
  parseCsv: (partnerId, csv) =>
    api.post(`/admin/partners/${partnerId}/bulk-invite/csv-parse`,
              { csv }, { skipWorkspaceHeader: true }),
};

export default api;

// Morgan — AI copilot (agent id morgan-v1)
export const morganApi = {
  me: () => api.get('/morgan/me'),
  sessions: () => api.get('/morgan/sessions'),
  createSession: (body) => api.post('/morgan/sessions', body || {}),
  getSession: (id) => api.get(`/morgan/sessions/${id}`),
  updateContext: (id, body) => api.patch(`/morgan/sessions/${id}/context`, body),
  upload: (id, file, fileType) => {
    const fd = new FormData();
    fd.append('file', file);
    if (fileType) fd.append('file_type', fileType);
    return api.post(`/morgan/sessions/${id}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  propose: (id, body) => api.post(`/morgan/sessions/${id}/actions`, body),
  confirm: (actionId) => api.post(`/morgan/actions/${actionId}/confirm`),
  reject: (actionId) => api.post(`/morgan/actions/${actionId}/reject`),
  prefill: (id, body) => api.post(`/morgan/sessions/${id}/prefill-case`, body),
  explain: (body) => api.post('/morgan/explain', body),
  checklist: (lane, caseType) => api.get('/morgan/evidence-checklist', { params: { lane, case_type: caseType } }),
  getSettings: () => api.get('/morgan/settings'),
  saveSettings: (body) => api.put('/morgan/settings', body),
  adminWorkspaces: () => api.get('/admin/morgan/workspaces', { skipWorkspaceHeader: true }),
  adminSaveWorkspace: (wsId, body) => api.put(`/admin/morgan/workspaces/${wsId}`, body, { skipWorkspaceHeader: true }),
  adminSavePlatform: (body) => api.put('/admin/morgan/platform', body, { skipWorkspaceHeader: true }),
  autonomy: () => api.get('/morgan/autonomy'),
  saveAutonomy: (body) => api.put('/morgan/autonomy', body),
  autonomousRun: (sid, body) => api.post(`/morgan/sessions/${sid}/autonomous-run`, body),
  pendingApprovals: (status = 'pending') => api.get('/morgan/pending-approvals', { params: { status } }),
  approvePending: (id) => api.post(`/morgan/pending-approvals/${id}/approve`),
  rejectPending: (id) => api.post(`/morgan/pending-approvals/${id}/reject`),
  revertOverride: (id, reason) => api.post(`/morgan/overrides/${id}/revert`, { reason }),
  digestSettings: () => api.get('/morgan/digest-settings'),
  saveDigestSettings: (body) => api.put('/morgan/digest-settings', body),
  digests: (params) => api.get('/morgan/digests', { params }),
  exportDigests: (params) => api.get('/morgan/digests/export', { params, responseType: 'blob' }),
  generateDigest: (body) => api.post('/morgan/digests/generate', body || { deliver: true }),
  digestEmailPreview: (id) => api.get(`/morgan/digests/${id}/email-preview`, { responseType: 'text' }),
};
export const caseBuilderApi = {
  resume: () => api.get('/morgan/case-builder/resume'),
  drafts: () => api.get('/morgan/case-builder/drafts'),
  startCard: () => api.get('/morgan/case-builder/start-card'),
  start: (body) => api.post('/morgan/case-builder/drafts', body),
  get: (id) => api.get(`/morgan/case-builder/drafts/${id}`),
  goto: (id, step) => api.post(`/morgan/case-builder/drafts/${id}/goto`, { step }),
  skip: (id) => api.post(`/morgan/case-builder/drafts/${id}/skip`),
  advance: (id) => api.post(`/morgan/case-builder/drafts/${id}/advance`),
  upload: (id, file, dataset) => {
    const fd = new FormData();
    fd.append('file', file);
    if (dataset) fd.append('dataset', dataset);
    return api.post(`/morgan/case-builder/drafts/${id}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  runMatching: (id) => api.post(`/morgan/case-builder/drafts/${id}/matching/run`),
  applyMatching: (id) => api.post(`/morgan/case-builder/drafts/${id}/matching/apply`),
  exceptions: (id, index) => api.get(`/morgan/case-builder/drafts/${id}/exceptions`, { params: { index } }),
  completeness: (id) => api.post(`/morgan/case-builder/drafts/${id}/completeness`),
  readiness: (id) => api.get(`/morgan/case-builder/drafts/${id}/readiness`),
};
export const caseEvidenceApi = {
  coverage: (caseId) => api.get(`/cases/${caseId}/evidence-coverage`),
  recompute: (caseId) => api.post(`/cases/${caseId}/evidence-coverage/recompute`),
  attach: (caseId, file, { caseItemId, documentType, source } = {}) => {
    const fd = new FormData();
    fd.append('file', file);
    if (caseItemId) fd.append('case_item_id', caseItemId);
    if (documentType) fd.append('document_type', documentType);
    fd.append('source', source || 'user_direct');
    return api.post(`/cases/${caseId}/evidence-docs`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000 });
  },
  override: (caseId, body) => api.post(`/cases/${caseId}/evidence-override`, body),
};
export const ledgerApi = {
  list: (params) => api.get('/compliance-ledger', { params }),
  exportReport: (params) => api.get('/compliance-ledger/export', { params, responseType: 'blob' }),
  verify: () => api.get('/compliance-ledger/verify', { skipWorkspaceHeader: true }),
};
export const filingHubApi = {
  list: (wsId, params) => api.get(`/workspaces/${wsId}/filing-hub`, { params }),
  resolve: (wsId, caseType, caseId) => api.get(`/workspaces/${wsId}/filing-hub/resolve`, { params: { case_type: caseType, case_id: caseId } }),
};
